/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject } from 'preact';
import { Ref } from 'preact/hooks';
import { ScrollDetail, SizingInfo, TableColumn } from '../Table';
import { TableHorizontalScrollPosition, TableVerticalScrollPosition } from '../../UNSAFE_TableView';
import { ViewportConfig } from '../../hooks/UNSAFE_useViewportIntersect';
import { findElementByKey } from '../../utils/PRIVATE_collectionUtils';
import {
  getColumnKey,
  getElementFromCell,
  getRowKey,
  getRowKeyForRowIndex,
  getScrollerBottomOffset,
  getScrollerTopOffset
} from './TableDomUtils';
import { tableCellStyles } from '../themes/TableCellStyles.css';
import { tableHeaderCellStyles } from '../themes/TableHeaderCellStyles.css';
import { tableFooterCellStyles } from '../themes/TableFooterCellStyles.css';

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
function _scrollColumnIntoView<K, D, C>(
  columnElement: HTMLElement,
  scrollerElement: HTMLElement,
  columnsArray: TableColumn<K, D, C>[],
  scrollbarWidth: number,
  isRtl: boolean,
  fullColumnWidths?: number[],
  location?: 'inView' | 'start' | 'end'
) {
  const columnKey = getColumnKey(columnElement);

  let columnIndex;
  for (let i = 0; i < columnsArray.length; i++) {
    if (columnsArray[i].key === columnKey) {
      columnIndex = i;
      break;
    }
  }
  if (columnIndex != null && fullColumnWidths != null) {
    let startOffset = 0;
    let endOffset = 0;

    const stickyStartColumns = getStickyColumnIndicies(columnsArray, true);
    for (let i = 0; i < stickyStartColumns.length; i++) {
      const stickyIndex = stickyStartColumns[i];
      if (stickyIndex < columnIndex) {
        startOffset += fullColumnWidths[stickyIndex];
      } else {
        break;
      }
    }
    const stickyEndColumns = getStickyColumnIndicies(columnsArray, false);
    for (let i = stickyEndColumns.length - 1; i > -1; i--) {
      const stickyIndex = stickyEndColumns[i];
      if (stickyIndex > columnIndex) {
        endOffset += fullColumnWidths[stickyIndex];
      } else {
        break;
      }
    }

    const horizontalOverflowDiff = _getHorizontalOverflowDiff(
      columnElement,
      scrollerElement,
      startOffset,
      endOffset,
      scrollbarWidth,
      isRtl
    );
    const leftOverflowDiff = horizontalOverflowDiff.left;
    const hasLeftOverflow = leftOverflowDiff >= 0;
    const rightOverflowDiff = horizontalOverflowDiff.right;
    const hasRightOverflow = rightOverflowDiff >= 0;

    // don't adjust scroll position if row has overflow in both directions
    if (hasLeftOverflow && hasRightOverflow) {
      return;
    }

    if (location == null || location === 'inView') {
      // if column fits fully in viewport, scroll overflow side into view
      // otherwise, scroll the shortest amount to fill viewport with column
      if (hasLeftOverflow) {
        if (Math.abs(rightOverflowDiff) > Math.abs(leftOverflowDiff)) {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft - leftOverflowDiff,
            false
          );
        } else {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft + rightOverflowDiff,
            true
          );
        }
      } else if (hasRightOverflow) {
        if (Math.abs(leftOverflowDiff) > Math.abs(rightOverflowDiff)) {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft + rightOverflowDiff,
            true
          );
        } else {
          scrollerElement.scrollLeft = _roundScrollValue(
            scrollerElement.scrollLeft - leftOverflowDiff,
            false
          );
        }
      }
    } else if (location === 'start') {
      scrollerElement.scrollLeft = _roundScrollValue(
        scrollerElement.scrollLeft - leftOverflowDiff,
        false
      );
    } else {
      scrollerElement.scrollLeft = _roundScrollValue(
        scrollerElement.scrollLeft + rightOverflowDiff,
        true
      );
    }
  }
}

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
const _scrollRowIntoView = (
  root: HTMLDivElement,
  tableId: string,
  rowElement: HTMLElement,
  scrollerElement: HTMLElement,
  scrollbarHeight: number,
  location?: 'inView' | 'top' | 'bottom'
) => {
  const topOffset = getScrollerTopOffset(root, tableId);
  const bottomOffset = getScrollerBottomOffset(root, tableId);
  const verticalOverflowDiff = _getVerticalOverflowDiff(
    rowElement,
    scrollerElement,
    topOffset,
    bottomOffset,
    scrollbarHeight
  );
  const topOverflowDiff = verticalOverflowDiff.top;
  const hasTopOverflow = topOverflowDiff >= 0;
  const bottomOverflowDiff = verticalOverflowDiff.bottom;
  const hasBottomOverflow = bottomOverflowDiff >= 0;

  // don't adjust scroll position if row has overflow in both directions
  if (hasTopOverflow && hasBottomOverflow) {
    return;
  }
  if (location == null || location === 'inView') {
    // if row fits fully in viewport, scroll overflow side into view
    // otherwise, scroll the shortest amount to fill viewport with row
    if (hasBottomOverflow) {
      if (Math.abs(topOverflowDiff) > Math.abs(bottomOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      }
    } else if (hasTopOverflow) {
      if (Math.abs(bottomOverflowDiff) > Math.abs(topOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      }
    }
  } else if (location === 'top') {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop - topOverflowDiff,
      false
    );
  } else {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop + bottomOverflowDiff,
      true
    );
  }
};

/**
 * Helper method to round a desired scrollTop or scrollLeft value to the nearest valid value
 * depending on the client browser.
 */
const _roundScrollValue = (value: number, isOver: boolean) => {
  // use Math.floor() and Math.ceil() to ensure we round to nearest containing valid
  // pixel value in either direction as valid scrollTop values are only integers
  return isOver ? Math.ceil(value) : Math.floor(value);
};

/**
 * A helper to get the horizontal overflow diff of the given element
 */
const _getHorizontalOverflowDiff = (
  element: HTMLElement,
  scrollerElement: HTMLElement,
  startOffset: number,
  endOffset: number,
  scrollbarWidth: number,
  isRtl: boolean
) => {
  const elementRect = element.getBoundingClientRect();
  const scrollerRect = scrollerElement.getBoundingClientRect();

  if (isRtl) {
    return {
      left: scrollerRect.left - elementRect.left + endOffset + scrollbarWidth,
      right: elementRect.right - scrollerRect.right + startOffset
    };
  } else {
    return {
      left: scrollerRect.left - elementRect.left + startOffset,
      right: elementRect.right - scrollerRect.right + endOffset + scrollbarWidth
    };
  }
};

/**
 * A helper to get the vertical overflow diff of the given element
 */
export const _getVerticalOverflowDiff = (
  element: HTMLElement,
  scrollerElement: HTMLElement,
  topOffset: number,
  bottomOffset: number,
  scrollbarHeight: number
) => {
  const elementRect = element.getBoundingClientRect();
  const scrollerRect = scrollerElement.getBoundingClientRect();

  return {
    top: scrollerRect.top - elementRect.top + topOffset,
    bottom: elementRect.bottom - scrollerRect.bottom + bottomOffset + scrollbarHeight
  };
};

/**
 * Helper to set the horizontal scroll position on the Table.
 */
export function setHorizontalScrollPosition<K, D, C>(
  root: HTMLDivElement,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  isRtl: boolean,
  horizontalScrollPosition?: TableHorizontalScrollPosition<C>
) {
  const x = _getScrollLeftFromScrollPosition(
    root,
    columnsArray,
    fullColumnWidths,
    horizontalScrollPosition
  );
  root.scrollLeft = x != null ? (isRtl ? -x : x) : 0;
}

/**
 * Helper to set the vertical scroll position on the Table.
 */
export function setVerticalScrollPosition<K>(
  root: HTMLDivElement,
  tableId: string,
  verticalScrollPosition?: TableVerticalScrollPosition<K>
) {
  const y = _getScrollTopFromScrollPosition(root, tableId, verticalScrollPosition);
  root.scrollTop = y != null ? y : 0;
}

/**
 * Helper to determine the 'x' value of a given scroll position.
 */
function _getScrollLeftFromScrollPosition<K, D, C>(
  root: HTMLDivElement,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  scrollPosition?: TableHorizontalScrollPosition<C>
) {
  if (scrollPosition != null) {
    let columnKey;
    let useColumn = false;
    if (scrollPosition.columnKey != null) {
      // columnKey takes precedence over everything else
      useColumn = true;
      columnKey = scrollPosition.columnKey;
    } else if (scrollPosition.offsetX != null && scrollPosition.x == null) {
      // offsetX can be used on its own if it is the only horizontal aspect provided
      useColumn = true;
      const currentHorizontalScrollPosition = getHorizontalScrollPosition(
        root,
        columnsArray,
        fullColumnWidths
      );
      columnKey = currentHorizontalScrollPosition.columnKey;
    }
    if (useColumn) {
      if (columnKey != null) {
        let x = 0;
        for (let i = 0; i < fullColumnWidths.length; i++) {
          if (columnsArray[i].key === columnKey) {
            return x + (scrollPosition.offsetX != null ? scrollPosition.offsetX : 0);
          }
          if (columnsArray[i].value.stickyEdge !== 'start') {
            x += fullColumnWidths[i];
          }
        }
      }
    } else if (scrollPosition.x != null) {
      return scrollPosition.x;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}

/**
 * Helper to determine the 'y' value of a given scroll position.
 */
function _getScrollTopFromScrollPosition<K>(
  root: HTMLDivElement,
  tableId: string,
  scrollPosition?: TableVerticalScrollPosition<K>
) {
  if (scrollPosition != null) {
    let rowKey;
    let useRow = false;
    if (scrollPosition.rowKey != null) {
      // rowKey takes precedence over everything else
      useRow = true;
      rowKey = scrollPosition.rowKey;
    } else if (scrollPosition.offsetY != null && scrollPosition.y == null) {
      // offsetY can be used on its own if it is the only vertical aspect provided
      useRow = true;
      rowKey = getVerticalScrollPosition(root, tableId).rowKey;
    }
    if (useRow) {
      if (rowKey != null) {
        let y = 0;
        const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
        for (let i = 0; i < rowElements.length; i++) {
          if (rowKey === getRowKey(rowElements[i] as HTMLElement)) {
            return y + (scrollPosition.offsetY != null ? scrollPosition.offsetY : 0);
          }
          y += rowElements[i].getBoundingClientRect().height;
        }
      }
    } else if (scrollPosition.y != null) {
      return scrollPosition.y;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}

/**
 * Helper to get the logical scroll position for the given scrollLeft value. If no scrollLeft value is given,
 * it will use the current scrollLeft value of the Table.
 */
export function getHorizontalScrollPosition<K, D, C>(
  root: HTMLDivElement,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  newScrollLeft?: number
) {
  // ensure scroll position x value is initialized
  const scrollLeft = Math.abs(newScrollLeft == null ? root.scrollLeft : newScrollLeft);

  const scrollPosition: TableHorizontalScrollPosition<C> = {
    x: scrollLeft,
    columnKey: undefined,
    offsetX: undefined
  };

  let columnEnd = 0;
  if (columnsArray.length > 0) {
    if (scrollLeft === 0) {
      scrollPosition.columnKey = columnsArray[0].key;
      scrollPosition.offsetX = 0;
      return scrollPosition;
    }
  }
  for (let i = 0; i < columnsArray.length; i++) {
    if (columnsArray[i].value.stickyEdge !== 'start') {
      columnEnd += fullColumnWidths[i];
    }
    if (scrollLeft < columnEnd) {
      scrollPosition.columnKey = columnsArray[i].key;
      scrollPosition.offsetX = fullColumnWidths[i] + scrollLeft - columnEnd;
      break;
    }
  }
  return scrollPosition;
}

/**
 * Helper to get the logical scroll position for the given scrollTop value. If no scrollTop value is given,
 * it will use the current scrollTop value of the Table.
 */
export function getVerticalScrollPosition<K>(
  root: HTMLDivElement,
  tableId: string,
  newScrollTop?: number
) {
  // ensure scroll position y value is initialized
  const scrollTop = Math.abs(newScrollTop == null ? root.scrollTop : newScrollTop);

  const scrollPosition: TableVerticalScrollPosition<K> = {
    y: scrollTop,
    rowKey: undefined,
    offsetY: undefined
  };

  let rowBottom = 0;
  const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
  if (rowElements.length > 0) {
    if (scrollTop === 0) {
      scrollPosition.rowKey = getRowKeyForRowIndex(root, tableId, 0) as K;
      scrollPosition.offsetY = 0;
      return scrollPosition;
    }
  }
  for (let i = 0; i < rowElements.length; i++) {
    const rowHeight = rowElements[i].getBoundingClientRect().height;
    rowBottom += rowHeight;
    if (scrollTop < rowBottom) {
      scrollPosition.rowKey = getRowKeyForRowIndex(root, tableId, i) as K;
      scrollPosition.offsetY = rowHeight + scrollTop - rowBottom;
      break;
    }
  }
  return scrollPosition;
}

export function applyScrollDetail<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfo: SizingInfo,
  scrollDetail: ScrollDetail<K, C>,
  isRtl: boolean,
  fullColumnWidths?: number[],
  viewportConfig?: ViewportConfig
) {
  const scroller = viewportConfig?.scroller() || root;
  if (scrollDetail.cell.type === 'data') {
    const rowElement = findElementByKey(
      root,
      scrollDetail.cell.rowKey,
      `[data-oj-table-data-row='${tableId}']`
    ) as HTMLElement;
    if (rowElement != null) {
      _scrollRowIntoView(
        root,
        tableId,
        rowElement,
        scroller,
        sizingInfo.boxHeight! - sizingInfo.contentHeight!,
        scrollDetail.locationY
      );
    }
  }
  const cellElement = getElementFromCell(root, tableId, scrollDetail.cell);
  if (cellElement != null) {
    _scrollColumnIntoView(
      cellElement,
      scroller,
      columnsArray,
      sizingInfo.boxWidth! - sizingInfo.contentWidth!,
      isRtl,
      fullColumnWidths,
      scrollDetail.locationX
    );
  }
}

/**
 * Helper function to update the 'left' and 'right' values of all sticky columns to correspond
 * to the given sizing information.
 */
export function updateStickyColumns<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  currentStickyEdgesRef: Ref<{ start?: number; end?: number }>,
  isForce?: boolean
) {
  let stickyIndex;
  let stickyStartOffset = 0;
  let stickyEndOffset = 0;

  const stickyStartIndicies = getStickyColumnIndicies(columnsArray, true);
  for (let i = 0; i < stickyStartIndicies.length; i++) {
    stickyIndex = stickyStartIndicies[i];
    _applyStickyColumnOffset(root, tableId, isRtl, stickyIndex, stickyStartOffset, true);
    stickyStartOffset += fullColumnWidths[stickyIndex];
  }
  const stickyEndIndicies = getStickyColumnIndicies(columnsArray, false);
  for (let i = stickyEndIndicies.length - 1; i > -1; i--) {
    stickyIndex = stickyEndIndicies[i];
    _applyStickyColumnOffset(root, tableId, isRtl, stickyIndex, stickyEndOffset, false);
    stickyEndOffset += fullColumnWidths[stickyIndex];
  }

  updateStickyEdges(
    root,
    tableId,
    isRtl,
    columnsArray,
    fullColumnWidths,
    currentStickyEdgesRef,
    undefined,
    isForce
  );
}

/**
 * Helper function to get the column indicies corresponding to the given edge.
 */
export const getStickyColumnIndicies = <K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  isStart: boolean
) => {
  const stickyColumns = [];
  const edge = isStart ? 'start' : 'end';
  for (let i = 0; i < columnsArray.length; i++) {
    if (columnsArray[i].value.stickyEdge === edge) {
      stickyColumns.push(i);
    }
  }
  return stickyColumns;
};

/**
 * Helper function to update the 'left' and 'right' values of a specific column.
 */
const _applyStickyColumnOffset = (
  root: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  columnIndex: number,
  stickyOffset: number,
  isStart: boolean
) => {
  const styleProperty = (isStart && !isRtl) || (!isStart && isRtl) ? 'left' : 'right';
  const styleValue = `${stickyOffset}px`;

  const headerElements = root.querySelectorAll(`[data-oj-table-header-cell='${tableId}']`);
  const headerElement = headerElements[columnIndex] as HTMLElement;
  headerElement.style[styleProperty] = styleValue;

  const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
  for (let j = 0; j < rowElements.length; j++) {
    const rowElement = rowElements[j] as HTMLElement;
    const cellElements = rowElement.querySelectorAll(`[data-oj-table-data-cell='${tableId}']`);
    const cellElement = cellElements[columnIndex] as HTMLElement;
    cellElement.style[styleProperty] = styleValue;
  }

  const footerElements = root.querySelectorAll(`[data-oj-table-footer-cell='${tableId}']`);
  if (footerElements.length > 0) {
    const footerElement = footerElements[columnIndex] as HTMLElement;
    footerElement.style[styleProperty] = styleValue;
  }
};

/**
 * Helper function to update the sticky edges based on the given scrollLeft value.
 */
export function updateStickyEdges<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  columnsArray: TableColumn<K, D, C>[],
  fullColumnWidths: number[],
  currentStickyEdgesRef: Ref<{ start?: number; end?: number }>,
  scrollLeft?: number,
  isForce?: boolean
) {
  if (columnsArray.length > 0) {
    let i;
    let currentScrollIndex;
    const newScrollPosition = getHorizontalScrollPosition(
      root,
      columnsArray,
      fullColumnWidths,
      scrollLeft
    );
    for (i = 0; i < columnsArray.length; i++) {
      if (columnsArray[i].key === newScrollPosition.columnKey) {
        currentScrollIndex = i;
        break;
      }
    }

    let startIndex;
    // browser zoom levels cause rounding issues where the start scroll
    // position may never be reached, but it should always be within 1
    if (newScrollPosition.x! >= 1) {
      const stickyStartIndicies = getStickyColumnIndicies(columnsArray, true);
      for (i = 0; i < stickyStartIndicies.length; i++) {
        const currentIndex = stickyStartIndicies[i];
        if (currentIndex < currentScrollIndex!) {
          startIndex = currentIndex;
        } else {
          break;
        }
      }
    }
    _updateStickyEdge(root, tableId, isRtl, currentStickyEdgesRef, true, startIndex, isForce);

    let endIndex;
    let currentWidth = 0;
    const maxScrollPos = root.scrollWidth - root.clientWidth;
    const endOverflow = maxScrollPos - newScrollPosition.x!;
    // browser zoom levels cause rounding issues where the max scroll
    // position may never be reached, but it should always be within 1
    if (endOverflow >= 1) {
      const stickyEndIndicies = getStickyColumnIndicies(columnsArray, false);
      for (let i = columnsArray.length - 1; i > -1; i--) {
        if (stickyEndIndicies.indexOf(i) !== -1) {
          endIndex = i;
        } else {
          currentWidth += fullColumnWidths[i];
          if (currentWidth > endOverflow) {
            break;
          }
        }
      }
    }
    _updateStickyEdge(root, tableId, isRtl, currentStickyEdgesRef, false, endIndex, isForce);
  }
}

/**
 * Helper function to update a specific sticky edge of the Table.
 */
const _updateStickyEdge = (
  root: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  currentStickyEdgesRef: Ref<{ start?: number; end?: number }>,
  isStart: boolean,
  edgeIndex?: number,
  isForce?: boolean
) => {
  if (currentStickyEdgesRef.current != null) {
    const currentEdgeIndex = isStart
      ? currentStickyEdgesRef.current.start
      : currentStickyEdgesRef.current.end;
    if (currentEdgeIndex !== edgeIndex || isForce) {
      if (currentEdgeIndex != null) {
        _addRemoveStickyEdgeStyle(root, tableId, isRtl, isStart, false, currentEdgeIndex);
      }
      if (edgeIndex != null) {
        _addRemoveStickyEdgeStyle(root, tableId, isRtl, isStart, true, edgeIndex);
      }
      if (isStart) {
        currentStickyEdgesRef.current.start = edgeIndex;
      } else {
        currentStickyEdgesRef.current.end = edgeIndex;
      }
    }
  }
};

/**
 * Helper function to apply or remove edge styling from a specific column.
 */
const _addRemoveStickyEdgeStyle = (
  root: HTMLDivElement,
  tableId: string,
  isRtl: boolean,
  isStart: boolean,
  isAdd: boolean,
  edgeIndex: number
) => {
  const isLeft = (isRtl && isStart) || (!isRtl && !isStart);
  const modifierFunc = isAdd ? 'add' : 'remove';

  const headerElements = root.querySelectorAll(`[data-oj-table-header-cell='${tableId}']`);
  const headerElement = headerElements[edgeIndex] as HTMLElement;
  headerElement.classList[modifierFunc](
    isLeft ? tableHeaderCellStyles.stickyEdgeLeft : tableHeaderCellStyles.stickyEdgeRight,
    isStart ? tableHeaderCellStyles.stickyEdgeStart : tableHeaderCellStyles.stickyEdgeEnd
  );

  const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
  for (let j = 0; j < rowElements.length; j++) {
    const rowElement = rowElements[j] as HTMLElement;
    const cellElements = rowElement.querySelectorAll(`[data-oj-table-data-cell='${tableId}']`);
    const cellElement = cellElements[edgeIndex] as HTMLElement;
    cellElement.classList[modifierFunc](
      isLeft ? tableCellStyles.stickyEdgeLeft : tableCellStyles.stickyEdgeRight,
      isStart ? tableCellStyles.stickyEdgeStart : tableCellStyles.stickyEdgeEnd
    );
  }

  const footerElements = root.querySelectorAll(`[data-oj-table-footer-cell='${tableId}']`);
  if (footerElements.length > 0) {
    const footerElement = footerElements[edgeIndex] as HTMLElement;
    footerElement.classList[modifierFunc](
      isLeft ? tableFooterCellStyles.stickyEdgeLeft : tableFooterCellStyles.stickyEdgeRight,
      isStart ? tableFooterCellStyles.stickyEdgeStart : tableFooterCellStyles.stickyEdgeEnd
    );
  }
};

/**
 * Helper function to get the viewport config. TODO: this needs help for table still
 */
export const getViewportConfig = (
  outerTableRef: RefObject<HTMLElement>,
  config?: ViewportConfig
) => {
  return (
    config ?? {
      scroller: () => {
        return outerTableRef.current;
      }
    }
  );
};
